import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { api, formatApiError } from "@/lib/api";
import { EMAIL, PHONE, PHONE_HREF } from "@/lib/site";
import { RouteMap } from "./RouteMap";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, MapPin, Phone, Send, Store, Truck } from "lucide-react";

const TOPICS = ["Brennholz", "Bauholz", "Forstdienstleistungen", "Holzkunst", "Bewerbung", "Sonstiges"];
const EMPTY_FORM = { name: "", email: "", street: "", zip: "", city: "", phone: "", topic: "", message: "" };

export const Kontakt = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [sending, setSending] = useState(false);
    const [ort, setOrt] = useState("");
    const [dist, setDist] = useState(null);
    const [checking, setChecking] = useState(false);

    const checkDistance = async () => {
        if (!ort.trim()) return;
        setChecking(true);
        setDist(null);
        try {
            const r = await api.get(`/distance?ort=${encodeURIComponent(ort.trim())}`);
            setDist(r.data);
            const isZip = /^\d{5}$/.test(ort.trim());
            setForm((f) => ({ ...f, zip: isZip ? ort.trim() : f.zip, city: r.data.label }));
            toast.success(`${r.data.label} wurde unten ins Formular übernommen.`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setChecking(false);
        }
    };

    const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

    useEffect(() => {
        const onPrefill = (e) => {
            setForm((f) => ({ ...f, ...e.detail }));
            toast.success("Angaben aus dem Rechner übernommen — bitte noch Name und E-Mail ergänzen.");
        };
        window.addEventListener("contact:prefill", onPrefill);
        return () => window.removeEventListener("contact:prefill", onPrefill);
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.topic) {
            toast.error("Bitte wählen Sie ein Anliegen aus.");
            return;
        }
        setSending(true);
        try {
            await api.post("/contact", form);
            toast.success("Vielen Dank! Ihre Anfrage ist eingegangen — ich melde mich schnellstmöglich.");
            setForm(EMPTY_FORM);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="kontakt" className="mx-auto max-w-7xl px-6 py-24 lg:py-32" data-testid="kontakt-section">
            <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
                <Reveal>
                    <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#C88A45]">Kontakt</p>
                    <h2 className="mb-8 font-display text-4xl font-extrabold uppercase tracking-tight text-[#EDE6DA] sm:text-5xl">
                        Sprechen wir über <span className="text-[#C88A45]">Ihr Holz.</span>
                    </h2>
                    <div className="space-y-6">
                        <a data-testid="contact-phone" href={PHONE_HREF} className="flex items-center gap-4 text-[#EDE6DA] transition-colors duration-200 hover:text-[#C88A45]">
                            <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#2E2517] text-[#C88A45]"><Phone size={18} /></span>
                            <span className="text-lg font-semibold">{PHONE}</span>
                        </a>
                        <a data-testid="contact-email" href={`mailto:${EMAIL}`} className="flex items-center gap-4 text-[#EDE6DA] transition-colors duration-200 hover:text-[#C88A45]">
                            <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#2E2517] text-[#C88A45]"><Mail size={18} /></span>
                            <span className="text-lg font-semibold">{EMAIL}</span>
                        </a>
                        <div className="flex items-start gap-4">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#2E2517] text-[#C88A45]"><MapPin size={18} /></span>
                            <div>
                                <p className="text-lg font-semibold text-[#EDE6DA]">Einsatzgebiet</p>
                                <p className="mt-1 text-sm leading-relaxed text-[#9C917D]" data-testid="contact-regions">
                                    Von der Uckermark über Berlin bis zur Mecklenburgischen Seenplatte und Mecklenburg-Strelitz — Lieferung bis ca. 150 km ab Templin.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#2E2517] text-[#C88A45]"><Truck size={18} /></span>
                            <div>
                                <p className="text-lg font-semibold text-[#EDE6DA]">Lieferung mit 7,5-t-Lkw</p>
                                <p className="mt-1 text-sm leading-relaxed text-[#9C917D]">
                                    Direkt zu Ihnen nach Hause — in der gesamten Region.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#2E2517] text-[#C88A45]"><Store size={18} /></span>
                            <div>
                                <p className="text-lg font-semibold text-[#EDE6DA]">Abholung in Templin</p>
                                <p className="mt-1 text-sm leading-relaxed text-[#9C917D]" data-testid="contact-pickup">
                                    Möglich — aber nur nach Absprache.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="mt-10 rounded border border-[#2E2517] bg-[#1B150E] p-6" data-testid="distance-checker">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C88A45]">Entfernung prüfen</p>
                        <p className="mt-2 text-sm text-[#9C917D]">Wie weit sind Sie von Templin entfernt?</p>
                        <div className="mt-3 flex gap-2">
                            <Input
                                data-testid="distance-input"
                                value={ort}
                                onChange={(e) => setOrt(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && checkDistance()}
                                placeholder="Ihr Ort oder PLZ"
                                className="border-[#2E2517] bg-[#120E09] text-[#EDE6DA]"
                            />
                            <button
                                data-testid="distance-button"
                                onClick={checkDistance}
                                disabled={checking}
                                className="shrink-0 rounded-full bg-[#C88A45] px-5 py-2 text-xs font-bold uppercase tracking-wide text-[#120E09] transition-colors duration-200 hover:bg-[#DBA057] disabled:opacity-50"
                            >
                                {checking ? "…" : "Prüfen"}
                            </button>
                        </div>
                        {dist && (
                            <p className="mt-3 text-sm text-[#D9D2C4]" data-testid="distance-result">
                                <span className="font-semibold text-[#EDE6DA]">{dist.label}</span> ist ca.{" "}
                                <span className="font-semibold text-[#C88A45]">{dist.km} km</span> von Templin entfernt —{" "}
                                {dist.in_range ? "Lieferung kein Problem." : "das ist über 150 km — fragen Sie trotzdem gern direkt an."}
                            </p>
                        )}
                        {dist && (
                            <div className="mt-4 overflow-hidden rounded border border-[#2E2517]" data-testid="distance-map">
                                <RouteMap dest={{ lat: dist.lat, lon: dist.lon, label: dist.label }} route={dist.route} />
                            </div>
                        )}
                    </div>
                    <div id="team" className="mt-6 scroll-mt-28 rounded border border-[#2E2517] bg-[#1B150E] p-6" data-testid="jobs-note">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C88A45]">Verstärkung gesucht</p>
                        <p className="mt-3 text-sm leading-relaxed text-[#D9D2C4]">
                            Werden Sie Teil des KischioWood-Teams — zum Beispiel bei Holzarbeiten & Spalten
                            oder als Kraftfahrer.
                        </p>
                        <a
                            data-testid="jobs-mail-link"
                            href="mailto:kischio@yahoo.de?subject=Bewerbung%20bei%20KischioWood"
                            className="mt-3 inline-block text-sm font-semibold text-[#C88A45] transition-colors duration-200 hover:text-[#DBA057]"
                        >
                            Kurze E-Mail mit Lebenslauf genügt →
                        </a>
                    </div>
                </Reveal>

                <Reveal delay={0.15}>
                    <form onSubmit={submit} className="rounded border border-[#2E2517] bg-[#1B150E] p-8" data-testid="contact-form">
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                            <div>
                                <label htmlFor="contact-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Name *</label>
                                <Input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className="border-[#2E2517] bg-[#120E09] text-[#EDE6DA]" placeholder="Ihr Name" />
                            </div>
                            <div>
                                <label htmlFor="contact-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">E-Mail *</label>
                                <Input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} className="border-[#2E2517] bg-[#120E09] text-[#EDE6DA]" placeholder="ihre@mail.de" />
                            </div>
                        </div>
                        <div className="mt-5">
                            <label htmlFor="contact-street" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Adresse (Lieferort) *</label>
                            <Input id="contact-street" data-testid="contact-street-input" required value={form.street} onChange={set("street")} className="border-[#2E2517] bg-[#120E09] text-[#EDE6DA]" placeholder="Straße & Hausnummer" />
                            <div className="mt-3 grid grid-cols-[110px_1fr] gap-3">
                                <Input id="contact-zip" data-testid="contact-zip-input" required inputMode="numeric" maxLength={5} value={form.zip} onChange={set("zip")} className="border-[#2E2517] bg-[#120E09] text-[#EDE6DA]" placeholder="PLZ" />
                                <Input id="contact-city" data-testid="contact-city-input" required value={form.city} onChange={set("city")} className="border-[#2E2517] bg-[#120E09] text-[#EDE6DA]" placeholder="Wohnort" />
                            </div>
                        </div>
                        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                            <div>
                                <label htmlFor="contact-phone" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Telefon</label>
                                <Input id="contact-phone" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className="border-[#2E2517] bg-[#120E09] text-[#EDE6DA]" placeholder="Optional" />
                            </div>
                            <div>
                                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Anliegen *</label>
                                <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                                    <SelectTrigger data-testid="contact-topic-select" className="border-[#2E2517] bg-[#120E09] text-[#EDE6DA]">
                                        <SelectValue placeholder="Bitte wählen" />
                                    </SelectTrigger>
                                    <SelectContent className="border-[#2E2517] bg-[#1B150E] text-[#EDE6DA]">
                                        {TOPICS.map((t) => (
                                            <SelectItem key={t} value={t} data-testid={`contact-topic-${t.toLowerCase()}`}>{t}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="mt-5">
                            <label htmlFor="contact-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-[#9C917D]">Nachricht *</label>
                            <Textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} className="border-[#2E2517] bg-[#120E09] text-[#EDE6DA]" placeholder="z. B. 10 SRM Laubholz, waldfrisch, Lieferung …" />
                        </div>
                        <button
                            data-testid="contact-submit-button"
                            type="submit"
                            disabled={sending}
                            className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-[#C88A45] py-3 text-sm font-bold uppercase tracking-wide text-[#120E09] transition-[background-color,transform,opacity] duration-200 hover:bg-[#DBA057] active:scale-95 disabled:opacity-50"
                        >
                            <Send size={15} /> {sending ? "Wird gesendet …" : "Anfrage senden"}
                        </button>
                    </form>
                </Reveal>
            </div>
        </section>
    );
};
